/**
 * Service-category reference data + helpers, ported VERBATIM from trexa-web.html
 * (`service_categories`, `CATG`, `CATICON`, `cic`). Mirrors the DB `ServiceCategory`
 * table (code/label/color). Category color/icon drive pills, chips and avatars.
 */
export interface ServiceCategoryRef {
  id: number;
  code: string;
  label: string;
  color: string;
  sortOrder: number;
}

export const SERVICE_CATEGORIES: ServiceCategoryRef[] = [
  // `full` is a PACKAGE TYPE, not a business category — a vendor never signs up as
  // "Full Package". Anything that asks a vendor what business they are in must read
  // BUSINESS_CATEGORIES instead (mockup renderOnboardCats filters it out the same way).
  { id: 9, code: 'full',    label: 'Full Package',      color: '#7A3FC4', sortOrder: 0 },
  { id: 1, code: 'hall',    label: 'Event Hall',        color: '#6C5CE7', sortOrder: 1 },
  { id: 2, code: 'caterer', label: 'Catering',          color: '#0B7C8C', sortOrder: 2 },
  { id: 3, code: 'photo',   label: 'Photography',       color: '#E0793C', sortOrder: 3 },
  { id: 4, code: 'makeup',  label: 'Makeup & Andaman',  color: '#D6457C', sortOrder: 4 },
  { id: 5, code: 'busana',  label: 'Baju Pengantin',    color: '#9B59B6', sortOrder: 5 },
  { id: 6, code: 'decor',   label: 'Decoration',        color: '#16A37B', sortOrder: 6 },
  { id: 7, code: 'video',   label: 'Videography',       color: '#2D7DD2', sortOrder: 7 },
  { id: 8, code: 'mc',      label: 'MC / Host',         color: '#E0A93C', sortOrder: 8 },
];

/** The `full` package type — bundles other services, never a vendor's own trade. */
export const FULL_PACKAGE_CODE = 'full';

/**
 * Categories a vendor can actually BE (signup, onboarding, admin vendor edit, partner
 * lists). Excludes `full`, which only ever describes a package (mockup: "Full Package is
 * a package type, not a business category").
 */
export const BUSINESS_CATEGORIES: ServiceCategoryRef[] =
  SERVICE_CATEGORIES.filter(c => c.code !== FULL_PACKAGE_CODE);

/** CATG: code → { label, color }. */
export const CATG: Record<string, { label: string; color: string }> =
  SERVICE_CATEGORIES
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .reduce((acc, c) => { acc[c.code] = { label: c.label, color: c.color }; return acc; },
      {} as Record<string, { label: string; color: string }>);

/** CATICON: code → inner SVG path markup for the category icon. */
export const CATICON: Record<string, string> = {
  full:    '<path d="M12 2 3 6.5v11L12 22l9-4.5v-11L12 2zm0 2.3 6.5 3.2L12 10.7 5.5 7.5 12 4.3zM5 9.3l6 3v7.4l-6-3V9.3zm8 10.4v-7.4l6-3v7.4l-6 3z"/>',
  hall:    '<path d="M12 2 2 7v2h20V7L12 2zM5 11h2v6H5v-6zm4 0h2v6H9v-6zm4 0h2v6h-2v-6zm4 0h2v6h-2v-6zM3 18h18v3H3v-3z"/>',
  caterer: '<path d="M11 3h2v2h-2zM4 11a8 8 0 0 1 16 0zM2 13h20v2H2z"/>',
  photo:   '<path d="M9 4 7.5 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.5L15 4H9zm3 4a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>',
  makeup:  '<path d="M6 2 7.2 4.8 10 6 7.2 7.2 6 10 4.8 7.2 2 6 4.8 4.8zM17 7 17.9 9.1 20 10 17.9 10.9 17 13 16.1 10.9 14 10 16.1 9.1zM12 13.5 13 16 15.5 17 13 18 12 20.5 11 18 8.5 17 11 16z"/>',
  busana:  '<path d="M14 2a2 2 0 0 1-4 0H8L7 7l2 1-3 14h12l-3-14 2-1-1-5h-2z"/>',
  decor:   '<path d="M12 9.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 1 0 0-5.6zM12 4.8a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 1 0 0-5.6zM12 13.6a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 1 0 0-5.6zM7.6 9.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 1 0 0-5.6zM16.4 9.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 1 0 0-5.6z"/>',
  video:   '<path d="M4 5h12a1 1 0 0 1 1 1v4l4-3v10l-4-3v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z"/>',
  mc:      '<path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.9V21h2v-3.1A7 7 0 0 0 19 11h-2z"/>',
};

/** cic: category code → inner SVG path markup (fallback: generic grid icon). */
export function cic(code: string): string {
  return CATICON[code] || '<path d="M4 4h16v4H4zm0 6h16v10H4z"/>';
}

/** cicPath: category code → just the path `d` data, for rendering `<path [attr.d]>` in a template */
export function cicPath(code: string): string {
  return /d="([^"]+)"/.exec(cic(code))?.[1] ?? '';
}

export function catLabel(code: string): string { return CATG[code]?.label ?? code; }
export function catColor(code: string): string { return CATG[code]?.color ?? 'var(--slate)'; }
